import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/pagoWompi.css";

import petfamLogo from "../assets/Petfamp.png";
import epaycoLogo from "../assets/epaycoLogo.png";

import chip from "../assets/chip.webp";
import visa from "../assets/visa.webp";
import mc from "../assets/mastercard.webp";
import amex from "../assets/amex.webp";

import config from "../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

function normalizeCardHolder(value) {
  return value
    .replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trimStart();
}

function normalizePlan(raw) {
  const v = String(raw || "").trim().toLowerCase();
  return v === "mensual" ? "mensual" : "mensual";
}

function onlyDigits(v, maxLen) {
  return String(v || "").replace(/\D/g, "").slice(0, maxLen);
}

function formatCardNumber(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

function normalizeInfluencerCode(raw) {
  return String(raw || "").trim().replace(/\s+/g, "");
}

function formatMoney(amount, currency) {
  const n = Number(amount || 0);

  if (currency === "COP") {
    const base = new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(n);

    const clean = base.replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();
    return `${clean} COP`;
  }

  const base = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  const clean = base.replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();
  return `${clean} USD`;
}

async function fetchFirstOkJson(urls, options) {
  let lastErr = null;

  for (const u of urls) {
    try {
      const r = await fetch(u, options);
      const data = await r.json().catch(() => null);

      if (!r.ok) {
        lastErr = new Error(data?.message || `HTTP ${r.status} en ${u}`);
        continue;
      }

      return { ok: true, url: u, data };
    } catch (e) {
      lastErr = e;
    }
  }

  return {
    ok: false,
    error: lastErr || new Error("No hubo respuesta válida."),
  };
}

export default function PagoEpayco() {
  const location = useLocation();
  const nav = useNavigate();

  const planId = normalizePlan(
    location.state?.planId || localStorage.getItem("petfam_selected_plan") || "mensual"
  );

  const email =
    location.state?.email ||
    localStorage.getItem("petfam_customer_email") ||
    localStorage.getItem("userEmail") ||
    "";

  useEffect(() => {
    if (!email) {
      nav("/crear-cuenta");
    }
  }, [email, nav]);

  const currency = "USD";
  const PRICE_USD = 3.19;

  const [cardType, setCardType] = useState("visa");
  const [discountCode, setDiscountCode] = useState("");

  const [acceptedEpaycoTerms, setAcceptedEpaycoTerms] = useState(false);
  const [acceptedPetfamTerms, setAcceptedPetfamTerms] = useState(false);

  const [inflChecking, setInflChecking] = useState(false);
  const [inflValid, setInflValid] = useState(false);
  const [inflName, setInflName] = useState("");
  const [inflMsg, setInflMsg] = useState("");

  const sw3AbortRef = useRef(null);
  const sw3DebounceRef = useRef(null);
  const payingRef = useRef(false);

  const [card, setCard] = useState({
    card_holder: "",
    number: "",
    exp_month: "",
    exp_year: "",
    cvc: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("");
  const [success, setSuccess] = useState(false);

  const showProcessingModal = loading;

  const cardLogo = cardType === "visa" ? visa : cardType === "mc" ? mc : amex;

  const shownNumber = card.number ? formatCardNumber(card.number) : "4575 6231 8229 0326";
  const shownName = card.card_holder?.trim() ? card.card_holder : "CARDHOLDER NAME";
  const shownExp = `${card.exp_month || "12"}/${card.exp_year || "27"}`;

  useEffect(() => {
    const code = normalizeInfluencerCode(discountCode);

    if (!code) {
      setInflChecking(false);
      setInflValid(false);
      setInflName("");
      setInflMsg("");
      if (sw3DebounceRef.current) clearTimeout(sw3DebounceRef.current);
      if (sw3AbortRef.current) sw3AbortRef.current.abort();
      return;
    }

    if (sw3DebounceRef.current) clearTimeout(sw3DebounceRef.current);

    sw3DebounceRef.current = setTimeout(async () => {
      try {
        setInflChecking(true);
        setInflValid(false);
        setInflName("");
        setInflMsg("Verificando código...");

        if (sw3AbortRef.current) sw3AbortRef.current.abort();
        const ac = new AbortController();
        sw3AbortRef.current = ac;

        const urls = [
          `${API_BASE_URL}/contacto/getinfluencer/${encodeURIComponent(code)}`,
          `${API_BASE_URL}/api/contacto/getinfluencer/${encodeURIComponent(code)}`,
          `${API_BASE_URL}/contacto/getinfluencer?codigo=${encodeURIComponent(code)}`,
          `${API_BASE_URL}/api/contacto/getinfluencer?codigo=${encodeURIComponent(code)}`,
        ];

        const res = await fetchFirstOkJson(urls, {
          method: "GET",
          signal: ac.signal,
          cache: "no-store",
        });

        if (!res.ok) {
          setInflValid(false);
          setInflName("");
          setInflMsg("No pudimos validar el código ahora.");
          return;
        }

        const out = res.data || {};
        const datos = out?.datos || out?.data || {};
        const exists = datos && Object.keys(datos).length > 0;

        const nombre =
          datos?.nombre ||
          datos?.name ||
          datos?.nombres ||
          datos?.Nombre ||
          "";

        if (exists) {
          setInflValid(true);
          setInflName(String(nombre || "").trim());
          setInflMsg("Código válido ✅");
        } else {
          setInflValid(false);
          setInflName("");
          setInflMsg("Código no existe.");
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
        setInflValid(false);
        setInflName("");
        setInflMsg("No pudimos validar el código ahora.");
      } finally {
        setInflChecking(false);
      }
    }, 450);

    return () => {
      if (sw3DebounceRef.current) clearTimeout(sw3DebounceRef.current);
    };
  }, [discountCode]);

  const hasValidCode = inflValid && normalizeInfluencerCode(discountCode).length > 0;

  const shownAmount = useMemo(() => {
    const base = PRICE_USD;
    const discounted = base * 0.9;
    return hasValidCode ? Math.round(discounted * 100) / 100 : base;
  }, [hasValidCode]);

  const hasDiscountApplied = hasValidCode && shownAmount !== PRICE_USD;

  function validateForm() {
    if (!email) return "No tengo el email del usuario.";

    if (!acceptedEpaycoTerms) return "Debes aceptar los términos de ePayco.";
    if (!acceptedPetfamTerms) return "Debes aceptar los términos de Petfam.";

    if (!card.card_holder.trim()) return "Escribe el nombre del titular.";

    const num = onlyDigits(card.number, 16);
    if (num.length < 13) return "Número de tarjeta incompleto.";

    const mm = onlyDigits(card.exp_month, 2);
    const yy = onlyDigits(card.exp_year, 4);

    if (mm.length !== 2) return "Mes inválido.";
    if (yy.length !== 2 && yy.length !== 4) return "Año inválido.";

    const cvc = onlyDigits(card.cvc, 4);
    if (cvc.length < 3) return "CVV inválido.";

    const code = normalizeInfluencerCode(discountCode);
    if (code && inflChecking) return "Espera: estamos verificando el código de descuento.";
    if (code && !inflValid) return "El código de descuento no es válido. Corrígelo o déjalo vacío.";

    return null;
  }

  function normalizeExpYearForApi(year) {
    const y = onlyDigits(year, 4);
    if (y.length === 2) return `20${y}`;
    return y;
  }

  async function handlePay() {
    if (payingRef.current) return;
    payingRef.current = true;

    const msg = validateForm();
    if (msg) {
      setError(msg);
      payingRef.current = false;
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      setStep("Guardando tarjeta segura en ePayco...");

      const payload = {
        customerEmail: email.trim().toLowerCase(),
        cardNumber: onlyDigits(card.number, 16),
        cardExpYear: normalizeExpYearForApi(card.exp_year),
        cardExpMonth: onlyDigits(card.exp_month, 2),
        cardCvc: onlyDigits(card.cvc, 4),
        cardHolderName: card.card_holder.trim(),
      };

      const r = await fetch(`${API_BASE_URL}/epayco/customer/setup-card`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseBody = await r.json().catch(() => null);

      if (!r.ok || !responseBody?.ok) {
        throw new Error(responseBody?.message || "No se pudo registrar la tarjeta en ePayco.");
      }

      localStorage.setItem("petfam_payment_provider", "EPAYCO");

      if (responseBody?.customerId) {
        localStorage.setItem("petfam_epayco_customer_id", String(responseBody.customerId));
      }

      if (responseBody?.cardTokenId) {
        localStorage.setItem("petfam_epayco_card_token_id", String(responseBody.cardTokenId));
      }

      setStep("Creando pago pendiente...");

      const reference = `EPAYCO-${Date.now()}`;
      const pendingResp = await fetch(`${API_BASE_URL}/epayco/payments/pending`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customerEmail: email.trim().toLowerCase(),
          reference,
          amountInCents: Math.round(shownAmount * 100),
          discountCode: hasValidCode ? normalizeInfluencerCode(discountCode) : null,
        }),
      });

      const pendingBody = await pendingResp.json().catch(() => null);

      if (!pendingResp.ok || !pendingBody?.ok) {
        throw new Error(pendingBody?.message || "No se pudo crear el pago pendiente.");
      }

      localStorage.setItem("petfam_epayco_reference", reference);

      setStep("Procesando cobro inicial...");

      const chargeResp = await fetch(`${API_BASE_URL}/epayco/payments/charge-initial`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reference,
        }),
      });

      const chargeBody = await chargeResp.json().catch(() => null);

      if (!chargeResp.ok || !chargeBody?.ok) {
        throw new Error(chargeBody?.message || "No se pudo realizar el cobro inicial.");
      }

      setStep("");
      setSuccess(true);
    } catch (e) {
      setError(e?.message || "Error procesando ePayco.");
    } finally {
      setLoading(false);
      payingRef.current = false;
    }
  }

  const inflCodeClean = normalizeInfluencerCode(discountCode);
  const allTermsAccepted = acceptedEpaycoTerms && acceptedPetfamTerms;

  return (
    <div className="pw-fullscreen">
      {showProcessingModal && (
        <div className="pw-modal-overlay" role="dialog" aria-modal="true" aria-label="Procesando pago">
          <div className="pw-modal">
            <p className="pw-modal-title">Procesando...</p>
            <p className="pw-modal-subtitle">{step || "Por favor espera un momento."}</p>
          </div>
        </div>
      )}

      <div className="pw-shell">
        <button
          type="button"
          className="pw-back"
          onClick={() => nav("/crear-cuenta")}
          aria-label="Volver"
          disabled={loading}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="pw-header">
          <img src={petfamLogo} alt="Petfam" />
          <span className="pw-x">x</span>
          <img src={epaycoLogo} alt="ePayco" />
        </div>

        <div className="pw-layout">
          <div className="pw-left">
            <div className="pw-card-stack">
              <div className="pw-card-back" aria-hidden="true" />
              <div className="pw-card-front">
                <img src={chip} className="pw-chip" alt="Chip" />
                <img src={cardLogo} className="pw-type-logo" alt="Card type" />
                <div className="pw-card-number">{shownNumber}</div>
                <div className="pw-card-footer">
                  <span className="pw-card-name">{shownName}</span>
                  <span className="pw-card-exp">{shownExp}</span>
                </div>
              </div>
            </div>
            {/* ✅ Mensaje soporte debajo de la tarjeta */}
  <div className="pw-support">
    Si tienes problemas con tu pago, contáctanos a{" "}
    <a href="mailto:soporte@petfam.club">soporte@petfam.club</a> y te ayudaremos a resolverlo.
  </div>
          </div>

          <div className="pw-right">
            <h2 className="pw-title">Detalles del pago</h2>
            <div className="pw-hint">Pago seguro con Epayco.</div>

            {error && <div className="pw-error">{error}</div>}

            {success ? (
              <div className="pw-success">
                <div style={{ marginBottom: 12 }}>Pago exitoso ✅</div>
                Revisa tu correo (puede llegar a Spam)
                <br />
                Te enviamos las credenciales para ingresar y crear el perfil de tu mascota.
                <button className="pw-pay" type="button" onClick={() => nav("/login")}>
                  OK
                </button>
              </div>
            ) : (
              <>

              <div className="pw-types" role="tablist" aria-label="Tipo de tarjeta">
                  <button
                    type="button"
                    className={`pw-type-btn ${cardType === "visa" ? "active" : ""}`}
                    onClick={() => setCardType("visa")}
                    disabled={loading}
                  >
                    <img src={visa} alt="Visa" loading="lazy" />
                  </button>

                  <button
                    type="button"
                    className={`pw-type-btn ${cardType === "mc" ? "active" : ""}`}
                    onClick={() => setCardType("mc")}
                    disabled={loading}
                  >
                    <img src={mc} alt="Mastercard" loading="lazy" />
                  </button>

                  <button
                    type="button"
                    className={`pw-type-btn ${cardType === "amex" ? "active" : ""}`}
                    onClick={() => setCardType("amex")}
                    disabled={loading}
                  >
                    <img src={amex} alt="American Express" loading="lazy" />
                  </button>
                </div>

                <label className="pw-label">Nombre y apellido del titular</label>
                <input
                  className="pw-input"
                  value={card.card_holder}
                  onChange={(e) => setCard((s) => ({ ...s, card_holder: normalizeCardHolder(e.target.value) }))}
                  placeholder="Tu nombre"
                  autoComplete="cc-name"
                  disabled={loading}
                />

                <label className="pw-label">Número de tarjeta</label>
                <input
                  className="pw-input"
                  value={card.number}
                  onChange={(e) => setCard((s) => ({ ...s, number: formatCardNumber(e.target.value) }))}
                  placeholder="4575 6231 8229 0326"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  disabled={loading}
                />

                <div className="pw-grid">
                  <div>
                    <label className="pw-label">Mes</label>
                    <input
                      className="pw-input"
                      value={card.exp_month}
                      onChange={(e) => setCard((s) => ({ ...s, exp_month: onlyDigits(e.target.value, 2) }))}
                      placeholder="MM"
                      inputMode="numeric"
                      autoComplete="cc-exp-month"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="pw-label">Año</label>
                    <input
                      className="pw-input"
                      value={card.exp_year}
                      onChange={(e) => setCard((s) => ({ ...s, exp_year: onlyDigits(e.target.value, 2) }))}
                      placeholder="YY"
                      inputMode="numeric"
                      autoComplete="cc-exp-year"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="pw-label">CVV</label>
                    <input
                      className="pw-input"
                      value={card.cvc}
                      onChange={(e) => setCard((s) => ({ ...s, cvc: onlyDigits(e.target.value, 4) }))}
                      placeholder="CVV"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      disabled={loading}
                    />
                  </div>

                  <div className="pw-influencer">
                    <label className="pw-label">Código de descuento (opcional)</label>
                    <input
                      className="pw-input"
                      placeholder="codigo"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                      disabled={loading}
                    />

                    {inflCodeClean ? (
                      <div className="pw-hint" style={{ marginTop: 6 }}>
                        {inflChecking ? (
                          <span>Verificando…</span>
                        ) : inflValid ? (
                          <span>
                            Código válido ✅ {inflName ? `(${inflName})` : ""}
                          </span>
                        ) : (
                          <span>{inflMsg || "Código no válido."}</span>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>


                <div className="pw-price" style={{ marginTop: 14 }}>
                  Importe: <b>{formatMoney(shownAmount, currency)}</b>
                  {hasDiscountApplied ? (
                    <div className="pw-discount-msg">¡Descuento aplicado del 10%!</div>
                  ) : null}
                </div>

                <label className="pw-terms">
  <input
    type="checkbox"
    checked={acceptedEpaycoTerms}
    onChange={(e) => setAcceptedEpaycoTerms(e.target.checked)}
    disabled={loading}
  />
  <span>
    Acepto los{" "}
    <a href="https://epayco.com/terminos-y-condiciones/" target="_blank" rel="noreferrer">
      términos y condiciones
    </a>{" "}
    de ePayco.
  </span>
</label>

<label className="pw-terms">
  <input
    type="checkbox"
    checked={acceptedPetfamTerms}
    onChange={(e) => setAcceptedPetfamTerms(e.target.checked)}
    disabled={loading}
  />
  <span>
    Acepto los{" "}
    <a href="/terminos" target="_blank" rel="noreferrer">
      términos y condiciones
    </a>{" "}
    y la{" "}
    <a href="/privacidad" target="_blank" rel="noreferrer">
      política de tratamiento de datos personales
    </a>{" "}
    de Vetia Group S.A.S (Petfam).
  </span>
</label>

                <button
                  className="pw-pay"
                  type="button"
                  onClick={handlePay}
                  disabled={loading || inflChecking || !allTermsAccepted}
                  title={
                    !acceptedEpaycoTerms
                      ? "Debes aceptar los términos de Epayco."
                      : !acceptedPetfamTerms
                      ? "Debes aceptar los términos y políticas de Petfam."
                      : inflChecking
                      ? "Verificando código influencer..."
                      : ""
                  }
                >
                  Pagar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}